import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  NgModule,
  OnChanges,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatCheckboxChange, MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';

import { MatSelectInfiniteScrollDirective } from '../../directives/mat-select-infinite-scroll/mat-select-infinite-scroll.directive';
import { SearchBarComponentModule } from '../search-bar/search-bar.component';

/**
 * Interface for items that can be used in the searchable selector.
 * Items must have at least a `name` property for display.
 */
export interface SearchableSelectorItem {
  id: string | number;
  name: string;
}

@Component({
    selector: 'ts-searchable-selector',
    templateUrl: './searchable-selector.component.html',
    styleUrls: ['./searchable-selector.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class SearchableSelectorComponent<T extends SearchableSelectorItem> implements OnChanges {
  @Input() loaded: boolean;
  @Input() options: T[];

  @Optional() @Input() searchTerm: string;
  @Optional() @Input() totalSelected: number;
  @Optional() @Input() selected: T[] = [];

  /**
   * Total number of items matching the current filter across ALL pages. When
   * provided, "select all" applies to the full result set: if not every page is
   * loaded yet, the checkbox asks the parent (via `selectAllAcrossPages`) to
   * load them and then selects everything once they arrive. When left undefined
   * the selector keeps its legacy behaviour ("select all" = only loaded rows).
   */
  @Optional() @Input() totalAvailable?: number;

  /** Accessible label for the selector */
  @Optional() @Input() ariaLabel = '';

  /** Data attribute for testing purposes */
  @Optional() @Input() dataCy = 'searchable-selector';

  /** Hint text shown when no items are selected */
  @Optional() @Input() hint = '';

  /** Whether to show the search bar. Defaults to true */
  @Optional() @Input() searchable = true;

  /** Custom message shown when no items are available. Defaults to 'info.no_items' translation key */
  @Optional() @Input() noItemsMessage: string;

  /** i18n key for an error message. When truthy, the selector renders an error state (red border collapsed; inline icon + message in the panel). The value is fed through the `translate` pipe — pass a key, not a literal. */
  @Optional() @Input() error: string | null = null;

  /** Property name to use for display text. Defaults to 'name' */
  @Optional() @Input() displayProperty: keyof T | 'name' = 'name';

  @Output() searchTermChanged = new EventEmitter<string>();
  @Output() lastItemReached = new EventEmitter<void>();
  @Output() added = new EventEmitter<T[]>();
  @Output() removed = new EventEmitter<T[]>();
  @Output() selectedChange = new EventEmitter<T[]>();

  /** Emitted when the user asks to select all but not every page is loaded yet. */
  @Output() selectAllAcrossPages = new EventEmitter<void>();

  /** Set while waiting for the parent to finish loading every page after a "select all". */
  private pendingSelectAll = false;

  public ngOnChanges(changes: SimpleChanges): void {
    // Once the parent has loaded the full result set in response to a "select
    // all", mark every option as selected.
    if (
      this.pendingSelectAll &&
      changes['options'] &&
      this.totalAvailable != null &&
      this.options.length >= this.totalAvailable
    ) {
      this.pendingSelectAll = false;
      this.selectionChanged(this.options);
    }
  }

  public get isIndeterminateChecked(): boolean {
    return this.selected.length > 0 && !this.isCompletelyChecked;
  }

  public get isCompletelyChecked(): boolean {
    if (this.pendingSelectAll) {
      return true;
    }
    if (this.totalAvailable != null) {
      return this.totalAvailable > 0 && this.selected.length >= this.totalAvailable;
    }
    return this.selected.length === this.options.length;
  }

  public get selectedCount(): number {
    return this.selected.length;
  }

  public get allOptions(): T[] {
    return this.options || [];
  }

  public get displayName(): string {
    if (!this.selected || this.selected.length === 0) {
      return this.hint;
    }

    // Return the alphabetically first item from selected items
    const sortedSelected = [...this.selected].sort((a, b) =>
      this.getDisplayValue(a).localeCompare(this.getDisplayValue(b))
    );
    return this.getDisplayValue(sortedSelected[0]);
  }

  /** Get the display value for an item using the displayProperty */
  public getDisplayValue(item: T): string {
    return String(item[this.displayProperty as keyof T] ?? item.name);
  }

  public selectionChanged(items: T[]) {
    const addedItems = items.filter((item) => !this.selected.some((s) => s.id === item.id));
    const removedItems = this.selected.filter(
      (item) => !items.some((s) => s.id === item.id) && this.options.some((s) => s.id === item.id)
    );
    const selectedNotInOptions = this.selected.filter((item) => !this.options.some((s) => s.id === item.id));
    this.selected = [...items, ...selectedNotInOptions];

    this.selectedChange.emit(this.selected);

    if (addedItems.length > 0) this.added.emit(addedItems);
    if (removedItems.length > 0) this.removed.emit(removedItems);
  }

  public toggleAllItems(event: MatCheckboxChange) {
    if (!event.checked) {
      this.pendingSelectAll = false;
      this.selectionChanged([]);
      return;
    }

    // Not every page is loaded: ask the parent to load the full result set and
    // defer the actual selection until the options arrive (see ngOnChanges).
    if (this.totalAvailable != null && this.options.length < this.totalAvailable) {
      this.pendingSelectAll = true;
      this.selectAllAcrossPages.emit();
      return;
    }

    this.selectionChanged(this.options);
  }

  public checkIfItemIsSelected(item1?: T, item2?: T) {
    return item1?.id === item2?.id;
  }
}

@NgModule({
  imports: [
    CommonModule,
    TranslateModule,
    MatFormFieldModule,
    MatSelectModule,
    MatCheckboxModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    SearchBarComponentModule,
    MatSelectInfiniteScrollDirective,
  ],
  declarations: [SearchableSelectorComponent],
  exports: [SearchableSelectorComponent],
})
export class SearchableSelectorComponentModule {}
